import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import AuthModal from "../components/AuthModal";
import {
  Zap,
  ArrowLeft,
  MapPin,
  Clock,
  Building2,
  DollarSign,
  Users,
  MessageSquare,
  Eye,
  Star,
  ChevronRight,
  Video,
  Send,
  Filter,
  CheckCircle,
  XCircle,
  UserCheck,
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Link2,
  Mail,
  Sun,
  Moon,
  Sunrise,
  Calendar,
  MessageCircle
} from "lucide-react";
import { Input } from "../components/ui/input";

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetter, setCoverLetter] = useState("");
  const [inviting, setInviting] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [filters, setFilters] = useState({
    min_match: "",
    min_experience: "",
    skills: "",
    location: "",
    industry: ""
  });

  const isRecruiter = user?.role === "recruiter";
  const isOwner = job?.recruiter_id === user?.user_id;

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setJob(data);
        
        // Fetch applicants if owner
        if (user?.role === "recruiter") {
          const appResponse = await fetch(`${API}/jobs/${jobId}/applicants`, {
            credentials: "include",
          });
          if (appResponse.ok) {
            const appData = await appResponse.json();
            setApplicants(appData);
          }
        }
      } else {
        toast.error("Job not found");
        navigate(-1);
      }
    } catch (error) {
      console.error("Failed to fetch job:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!showApplyForm) {
      setShowApplyForm(true);
      return;
    }
    setApplying(true);
    try {
      const formData = new FormData();
      if (resumeFile) formData.append("resume", resumeFile);
      if (coverLetter.trim()) formData.append("cover_letter", coverLetter);

      const response = await fetch(`${API}/jobs/${jobId}/apply`, {
        method: "POST",
        credentials: "include",
        ...(resumeFile || coverLetter.trim() ? { body: formData } : {}),
      });
      if (response.ok) {
        toast.success("Application submitted! AI is analyzing your fit...");
        setShowApplyForm(false);
        setResumeFile(null);
        setCoverLetter("");
        fetchJob();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to apply");
      }
    } catch (error) {
      toast.error("Failed to apply");
    } finally {
      setApplying(false);
    }
  };

  const startInterview = async (candidateId) => {
    try {
      const response = await fetch(`${API}/ai/interview/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ job_id: jobId, candidate_id: candidateId }),
      });
      if (response.ok) {
        const session = await response.json();
        navigate(`/interview/${session.session_id}`);
      } else {
        toast.error("Failed to start interview");
      }
    } catch (error) {
      toast.error("Failed to start interview");
    }
  };

  const sendInterviewInvitation = async (candidateId) => {
    setInviting(candidateId);
    try {
      const response = await fetch(`${API}/ai/interview/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          job_id: jobId, 
          candidate_id: candidateId,
          num_questions: 6,
          time_limit_minutes: 30
        }),
      });
      if (response.ok) {
        toast.success("Interview invitation sent!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setInviting(null);
    }
  };

  const applyFilters = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.min_match) params.append("min_match", filters.min_match);
      if (filters.min_experience) params.append("min_experience", filters.min_experience);
      if (filters.skills) params.append("skills", filters.skills);
      if (filters.location) params.append("location", filters.location);
      if (filters.industry) params.append("industry", filters.industry);

      const response = await fetch(`${API}/jobs/${jobId}/applicants?${params.toString()}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setApplicants(data);
        setShowFilters(false);
      }
    } catch (error) {
      console.error("Failed to filter applicants:", error);
    }
  };

  const clearFilters = () => {
    setFilters({ min_match: "", min_experience: "", skills: "", location: "", industry: "" });
    fetchJob();
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(applicationId);
    try {
      const response = await fetch(`${API}/jobs/${jobId}/applications/${applicationId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Application ${newStatus}`);
        // Update local state
        setApplicants(prev => prev.map(app => 
          app.application_id === applicationId 
            ? { ...app, status: newStatus }
            : app
        ));
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400",
      reviewed: "bg-blue-500/20 text-blue-400",
      shortlisted: "bg-green-500/20 text-green-400",
      interviewed: "bg-purple-500/20 text-purple-400",
      offered: "bg-emerald-500/20 text-emerald-400",
      rejected: "bg-red-500/20 text-red-400",
      hired: "bg-green-500/20 text-green-400",
      withdrawn: "bg-gray-500/20 text-gray-400"
    };
    return colors[status] || "bg-gray-500/20 text-gray-400";
  };

  // Clean up markdown symbols from job description for better readability
  const cleanDescription = (text) => {
    if (!text) return '';
    
    return text
      // Remove headers (##, ###, etc.)
      .replace(/^#{1,6}\s*/gm, '')
      // Remove bold markers (**)
      .replace(/\*\*/g, '')
      // Remove italic markers (* or _)
      .replace(/(?<!\*)\*(?!\*)/g, '')
      .replace(/(?<!_)_(?!_)/g, '')
      // Remove horizontal rules (---, ***, ___)
      .replace(/^[-*_]{3,}\s*$/gm, '')
      // Remove bullet point markers but keep the text
      .replace(/^[\s]*[-*+]\s+/gm, '• ')
      // Remove numbered list markers but keep the text
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // Remove code blocks markers (```)
      .replace(/```[\s\S]*?```/g, (match) => match.replace(/```/g, '').trim())
      // Remove inline code markers (`)
      .replace(/`([^`]+)`/g, '$1')
      // Remove link markdown [text](url) -> text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Clean up multiple newlines
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Social sharing functions
  const getJobUrl = () => {
    // Full absolute URL for social media sharing
    return `${window.location.origin}/api/j/${jobId}`;
  };

  const getShareText = () => {
    // Build location string (Country, State)
    const locationParts = [];
    if (job.location) {
      // Parse location - could be "City, State, Country" or just "Remote"
      const parts = job.location.split(', ');
      if (parts.length >= 2) {
        // Take state and country (last two parts)
        locationParts.push(parts.slice(-2).join(', '));
      } else {
        locationParts.push(job.location);
      }
    }
    
    // Build work hours string
    let workHours = '';
    if (job.work_timing_start && job.work_timing_end) {
      workHours = ` | ${job.work_timing_start} - ${job.work_timing_end}`;
    } else if (job.shift_type) {
      const shiftLabels = {
        first: 'Morning Shift',
        second: 'Afternoon Shift', 
        third: 'Night Shift',
        rotating: 'Rotating Shifts',
        flexible: 'Flexible Hours'
      };
      workHours = ` | ${shiftLabels[job.shift_type] || job.shift_type}`;
    }
    
    // Company name is required - use it directly
    const companyName = job.company_name || job.recruiter_company || 'Company';
    const location = locationParts.length > 0 ? ` in ${locationParts[0]}` : '';
    
    return `${job.title} at ${companyName}${location}${workHours}. Apply now!`;
  };

  // Track share event
  const trackShare = (platform) => {
    fetch(`${API}/analytics/share-track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ job_id: jobId, platform })
    }).catch(() => {});
  };

  const shareToTwitter = () => {
    trackShare("twitter");
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(getJobUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const shareToLinkedIn = () => {
    trackShare("linkedin");
    const url = encodeURIComponent(getJobUrl());
    const title = encodeURIComponent(job.title);
    const summary = encodeURIComponent(getShareText());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=600');
    setShowShareMenu(false);
  };

  const shareToFacebook = () => {
    trackShare("facebook");
    const url = encodeURIComponent(getJobUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const shareByEmail = () => {
    trackShare("email");
    const subject = encodeURIComponent(`Job Opportunity: ${job.title}`);
    const body = encodeURIComponent(`${getShareText()}\n\nApply here: ${getJobUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowShareMenu(false);
  };

  const shareToWhatsApp = () => {
    trackShare("whatsapp");
    const text = encodeURIComponent(`${getShareText()}\n\nApply here: ${getJobUrl()}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShareMenu(false);
  };

  const copyJobLink = async () => {
    trackShare("copy_link");
    try {
      await navigator.clipboard.writeText(getJobUrl());
      setLinkCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      trackShare("native");
      try {
        await navigator.share({
          title: job.title,
          text: getShareText(),
          url: getJobUrl()
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error("Failed to share");
        }
      }
    } else {
      setShowShareMenu(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-crimson flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">JOB DETAILS</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div 
                className="bg-obsidian-light border border-[#222] p-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h1 className="font-heading text-3xl font-bold mb-2">{job.title}</h1>
                    <div className="flex items-center gap-4 text-gray-400">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {job.industry}
                      </span>
                      {job.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-sm uppercase tracking-wider ${
                    job.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
                  }`}>
                    {job.status}
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="tag">
                    <Clock className="w-3 h-3 mr-1" />
                    {job.seniority_level}
                  </span>
                  <span className="tag">
                    {job.employment_type}
                  </span>
                  {job.salary_min && job.salary_max && (
                    <span className="tag">
                      <DollarSign className="w-3 h-3 mr-1" />
                      ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Work Schedule Info */}
                {(job.shift_type || job.work_timing_start || job.work_days?.length > 0) && (
                  <div className="mb-8 p-4 bg-obsidian border border-[#333] rounded">
                    <h3 className="font-heading text-sm font-bold mb-3 text-gray-400 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> WORK SCHEDULE
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {job.shift_type && (
                        <div className="flex items-center gap-2">
                          {job.shift_type === "first" && <Sunrise className="w-4 h-4 text-yellow-400" />}
                          {job.shift_type === "second" && <Sun className="w-4 h-4 text-orange-400" />}
                          {job.shift_type === "third" && <Moon className="w-4 h-4 text-blue-400" />}
                          {job.shift_type === "rotating" && <Clock className="w-4 h-4 text-purple-400" />}
                          {job.shift_type === "flexible" && <Calendar className="w-4 h-4 text-green-400" />}
                          <span className="text-sm text-gray-300 capitalize">
                            {job.shift_type === "first" ? "First Shift (Morning)" :
                             job.shift_type === "second" ? "Second Shift (Afternoon)" :
                             job.shift_type === "third" ? "Third Shift (Night)" :
                             job.shift_type === "rotating" ? "Rotating Shifts" :
                             job.shift_type === "flexible" ? "Flexible Hours" : job.shift_type}
                          </span>
                        </div>
                      )}
                      {job.work_timing_start && job.work_timing_end && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-crimson" />
                          <span className="text-sm text-gray-300">
                            {job.work_timing_start} - {job.work_timing_end}
                          </span>
                        </div>
                      )}
                      {job.work_days?.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Calendar className="w-4 h-4 text-crimson" />
                          <span className="text-sm text-gray-300">
                            {job.work_days.map(d => d.slice(0, 3).charAt(0).toUpperCase() + d.slice(1, 3)).join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Share Section */}
                <div className="mb-8 flex items-center gap-3">
                  <span className="text-gray-400 text-sm">Share this job:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={shareToTwitter}
                      className="p-2 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 transition-colors"
                      title="Share on Twitter/X"
                      data-testid="share-twitter"
                    >
                      <Twitter className="w-4 h-4 text-[#1DA1F2]" />
                    </button>
                    <button
                      onClick={shareToLinkedIn}
                      className="p-2 bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 border border-[#0A66C2]/30 transition-colors"
                      title="Share on LinkedIn"
                      data-testid="share-linkedin"
                    >
                      <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                    </button>
                    <button
                      onClick={shareToFacebook}
                      className="p-2 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border border-[#1877F2]/30 transition-colors"
                      title="Share on Facebook"
                      data-testid="share-facebook"
                    >
                      <Facebook className="w-4 h-4 text-[#1877F2]" />
                    </button>
                    <button
                      onClick={shareToWhatsApp}
                      className="p-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 transition-colors"
                      title="Share on WhatsApp"
                      data-testid="share-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4 text-[#25D366]" />
                    </button>
                    <button
                      onClick={shareByEmail}
                      className="p-2 bg-gray-500/10 hover:bg-gray-500/20 border border-gray-500/30 transition-colors"
                      title="Share via Email"
                      data-testid="share-email"
                    >
                      <Mail className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={copyJobLink}
                      className={`p-2 border transition-colors ${
                        linkCopied 
                          ? "bg-green-500/20 border-green-500/30" 
                          : "bg-crimson/10 hover:bg-crimson/20 border-crimson/30"
                      }`}
                      title="Copy Link"
                      data-testid="share-copy-link"
                    >
                      {linkCopied ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <Link2 className="w-4 h-4 text-crimson" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Skills */}
                {job.skills?.length > 0 && (
                  <div className="mb-8">
                    <h3 className="font-heading text-lg font-bold mb-3">REQUIRED SKILLS</h3>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-crimson/10 border border-crimson/30 text-crimson text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <h3 className="font-heading text-lg font-bold mb-3">JOB DESCRIPTION</h3>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <div className="whitespace-pre-wrap font-body text-gray-300 leading-relaxed">
                      {cleanDescription(job.description)}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Apply / Actions */}
              {(!user || (!isOwner && user?.role === "jobseeker")) && (
                <motion.div 
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <h3 className="font-heading text-lg font-bold mb-4">APPLY NOW</h3>
                  
                  {/* Apply Form (shown after first click) */}
                  {showApplyForm && (
                    <div className="space-y-4 mb-4" data-testid="apply-form">
                      <div>
                        <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Resume (PDF or Word)</label>
                        <input
                          type="file"
                          accept=".pdf,.docx"
                          onChange={(e) => setResumeFile(e.target.files[0] || null)}
                          className="w-full text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:border file:border-[#333] file:bg-obsidian file:text-crimson file:text-xs file:uppercase file:tracking-wider file:cursor-pointer"
                          data-testid="resume-upload"
                        />
                        <p className="text-[10px] text-gray-600 mt-1">Upload for better AI scoring (optional)</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Cover Letter (optional)</label>
                        <textarea
                          value={coverLetter}
                          onChange={(e) => setCoverLetter(e.target.value)}
                          placeholder="Tell the recruiter why you're a great fit..."
                          rows={3}
                          className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm resize-none"
                          data-testid="cover-letter-input"
                        />
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleApply}
                    disabled={applying}
                    className="w-full bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider py-6"
                    data-testid="apply-btn"
                  >
                    {applying ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : showApplyForm ? (
                      "Submit Application"
                    ) : (
                      "Apply Now"
                    )}
                  </Button>
                  <p className="text-gray-500 text-xs text-center mt-3">
                    {showApplyForm ? "Your resume & profile will be AI-scored against this job" : "Your profile will be shared with the recruiter"}
                  </p>
                  
                  {/* Resume Optimization */}
                  <div className="mt-4 pt-4 border-t border-[#333]">
                    <Button
                      onClick={() => navigate(`/resume-optimization/${jobId}`)}
                      variant="outline"
                      className="w-full border-[#333] text-white hover:border-crimson"
                      data-testid="optimize-resume-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Optimize Resume for This Job
                    </Button>
                    <p className="text-gray-500 text-xs text-center mt-2">
                      Get AI suggestions to improve your chances
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Applicants (for recruiters) */}
              {isOwner && (
                <motion.div 
                  className="bg-obsidian-light border border-[#222] p-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-bold">APPLICANTS</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-crimson font-bold">{applicants.length}</span>
                      <Button
                        onClick={() => setShowFilters(!showFilters)}
                        size="sm"
                        variant="outline"
                        className={`border-[#333] text-white ${showFilters ? "border-crimson" : ""}`}
                      >
                        <Filter className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Filters */}
                  {showFilters && (
                    <div className="mb-4 p-3 bg-obsidian border border-[#1a1a1a] space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Min Match %</label>
                          <Input
                            type="number"
                            value={filters.min_match}
                            onChange={(e) => setFilters(prev => ({ ...prev, min_match: e.target.value }))}
                            placeholder="e.g., 50"
                            className="bg-obsidian-light border-[#333] text-white h-8 text-sm"
                            data-testid="filter-min-match"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Min Experience (years)</label>
                          <Input
                            type="number"
                            value={filters.min_experience}
                            onChange={(e) => setFilters(prev => ({ ...prev, min_experience: e.target.value }))}
                            placeholder="e.g., 3"
                            className="bg-obsidian-light border-[#333] text-white h-8 text-sm"
                            data-testid="filter-min-experience"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Skills (comma-separated)</label>
                        <Input
                          value={filters.skills}
                          onChange={(e) => setFilters(prev => ({ ...prev, skills: e.target.value }))}
                          placeholder="e.g., React, Python"
                          className="bg-obsidian-light border-[#333] text-white h-8 text-sm"
                          data-testid="filter-skills"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Location</label>
                          <Input
                            value={filters.location}
                            onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                            placeholder="e.g., New York"
                            className="bg-obsidian-light border-[#333] text-white h-8 text-sm"
                            data-testid="filter-location"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Industry</label>
                          <Input
                            value={filters.industry}
                            onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                            placeholder="e.g., Technology"
                            className="bg-obsidian-light border-[#333] text-white h-8 text-sm"
                            data-testid="filter-industry"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={applyFilters} size="sm" className="flex-1 bg-crimson text-white text-xs" data-testid="apply-filters-btn">
                          Apply
                        </Button>
                        <Button onClick={clearFilters} size="sm" variant="outline" className="border-[#333] text-white text-xs" data-testid="clear-filters-btn">
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}

                  {applicants.length > 0 ? (
                    <div className="space-y-4">
                      {applicants.slice(0, 5).map((app, index) => (
                        <div
                          key={app.application_id}
                          className="p-4 bg-obsidian border border-[#222] hover:border-crimson/50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-bold">{app.user?.name || "Unknown"}</h4>
                              <p className="text-gray-400 text-sm">{app.profile?.headline || "No headline"}</p>
                              {app.years_experience > 0 && (
                                <p className="text-gray-500 text-xs">{app.years_experience} years experience</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center gap-1 px-2 py-1 bg-crimson/10 text-crimson text-sm">
                                <Star className="w-3 h-3" />
                                {app.match_score}%
                              </div>
                              <span className={`px-2 py-0.5 text-xs uppercase font-medium ${getStatusColor(app.status || 'pending')}`}>
                                {app.status || 'pending'}
                              </span>
                            </div>
                          </div>

                          {app.profile?.skills?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {app.profile.skills.slice(0, 3).map((skill, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-[#1a1a1a] text-xs text-gray-400">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Status Actions */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            <button
                              onClick={() => updateApplicationStatus(app.application_id, 'shortlisted')}
                              disabled={updatingStatus === app.application_id || app.status === 'shortlisted'}
                              className={`flex items-center gap-1 px-2 py-1 text-xs transition-colors ${
                                app.status === 'shortlisted' 
                                  ? 'bg-green-500/30 text-green-400 cursor-default' 
                                  : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                              }`}
                              data-testid={`shortlist-${app.application_id}`}
                            >
                              <UserCheck className="w-3 h-3" />
                              {app.status === 'shortlisted' ? 'Shortlisted' : 'Shortlist'}
                            </button>
                            <button
                              onClick={() => updateApplicationStatus(app.application_id, 'rejected')}
                              disabled={updatingStatus === app.application_id || app.status === 'rejected'}
                              className={`flex items-center gap-1 px-2 py-1 text-xs transition-colors ${
                                app.status === 'rejected' 
                                  ? 'bg-red-500/30 text-red-400 cursor-default' 
                                  : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                              }`}
                              data-testid={`reject-${app.application_id}`}
                            >
                              <XCircle className="w-3 h-3" />
                              {app.status === 'rejected' ? 'Rejected' : 'Reject'}
                            </button>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              onClick={() => sendInterviewInvitation(app.candidate_id)}
                              size="sm"
                              className="flex-1 bg-crimson hover:bg-crimson-hover text-white text-xs uppercase"
                              disabled={inviting === app.candidate_id}
                              data-testid={`invite-${app.application_id}`}
                            >
                              {inviting === app.candidate_id ? (
                                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3 h-3 mr-1" /> Invite Interview
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => navigate(`/candidate/${app.candidate_id}`)}
                              size="sm"
                              variant="outline"
                              className="border-[#333] hover:border-crimson text-white text-xs uppercase"
                              data-testid={`profile-${app.application_id}`}
                            >
                              <Eye className="w-3 h-3 mr-1" /> Profile
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-500">No applicants yet</p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Stats */}
              <motion.div 
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-heading text-lg font-bold mb-4">JOB STATS</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Posted</span>
                    <span className="text-white">{new Date(job.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Applicants</span>
                    <span className="text-crimson font-bold">{job.applicants?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Status</span>
                    <span className="text-green-400">{job.status}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultTab="register"
          message="Create a Rekrutor profile to apply for this job"
        />
      )}
    </div>
  );
};

export default JobDetails;
